import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useClickAway } from 'react-use';
import { Button, Flex } from '..';
import { InternalCalendarDateContext, LocaleContext, UserSelectedDateContext } from './contexts';
import { Calendar } from './calendar';
import { Fieldset } from '../fieldset';
import { SelectedDate } from './selectedDate';
import styles, { datePicker as datePickerClass } from './date-picker.module.css';
export const DatePicker = forwardRef(({ className, label, disablePast, disableFuture, locale = 'en', disabledDates, onDateChange, disabled, ...props }, forwardedRef) => {
    const [calendarVisible, setCalendarVisibility] = useState(false);
    const [clickedAway, setClickedAway] = useState(false);
    const currentDate = new Date();
    const [internalCalendarDate, setInternalCalendarDate] = useState(currentDate);
    const [userSelectedDate, setUserSelectedDate] = useState(currentDate);
    const ref = useRef(null);
    useClickAway(ref, () => {
        setClickedAway(true);
        setCalendarVisibility(false);
    });
    const handleDateChange = useCallback((date) => {
        if (!disabled && onDateChange)
            onDateChange(date);
    }, [disabled, onDateChange]);
    useEffect(() => {
        handleDateChange(userSelectedDate);
    }, [userSelectedDate]);
    const showCalendar = useCallback(() => {
        if (disabled)
            return;
        if (clickedAway) {
            setClickedAway(false);
            return;
        }
        setCalendarVisibility(true);
    }, [setCalendarVisibility, calendarVisible, clickedAway, setClickedAway]);
    const internalCalendarDateContextValue = useMemo(() => ({
        date: internalCalendarDate,
        setDate: setInternalCalendarDate
    }), [internalCalendarDate, setInternalCalendarDate]);
    const userSelectedDateContext = useMemo(() => ({
        date: userSelectedDate,
        setDate: setUserSelectedDate
    }), [userSelectedDate, setUserSelectedDate]);
    return (React.createElement(LocaleContext.Provider, { value: locale },
        React.createElement(InternalCalendarDateContext.Provider, { value: internalCalendarDateContextValue },
            React.createElement(UserSelectedDateContext.Provider, { value: userSelectedDateContext },
                React.createElement(Flex, { ref: forwardedRef, className: clsx(datePickerClass, className), ...props },
                    React.createElement(Fieldset, { label: label, disabled: disabled },
                        React.createElement(Button, { onClick: showCalendar, className: styles.activator, capitalize: false, kind: 'secondary-dark', contentAlign: 'space-between', noBorder: true, iconRight: true, icon: 'arrow-simple-down/16' },
                            React.createElement(SelectedDate, null))),
                    React.createElement(Calendar, { ref: ref, visible: calendarVisible, disableFuture: disableFuture, disablePast: disablePast, disabledDates: disabledDates }))))));
});
DatePicker.displayName = 'DatePicker';
